import { SalesforceIntegrationId } from 'module/api/integrations/salesforce/types';
import { useTranslate } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { IconShell } from 'module/ui/components/atoms/icon-shell';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { DraggableTable } from 'module/ui/components/molecules/draggable-table';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { Col, Row, Space, Typography } from 'antd';
import { useAutomationsListActions } from 'hooks/integrations/salesforce/use-automation-list-actions';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { EmptyState } from 'components/atoms/empty-state';
import { getSalesforceNewAutomationUrl } from 'components/pages/integrations/salesforce/utils/urls';
import { generateColumns } from './columns';

const getParam = (id: string | undefined): string => {
  return id === 'new' ? 'new' : id || '';
};

const { Text } = Typography;

export const AutomationList = () => {
  const { integrationId: _integrationId } =
    useParams<SalesforceIntegrationId>();
  const integrationId = getParam(_integrationId);
  const {
    automationList,
    automationListStatus,
    onChange,
    onDelete,
    onEdit,
    onSortChange,
  } = useAutomationsListActions();

  const translate = useTranslate();

  const columns = useMemo(
    () =>
      generateColumns({
        integrationId,
        handleDelete: onDelete,
        handleEnable: onChange,
        handleEdit: onEdit,
        translate: (key: string) => translate(key as any),
      }),
    [integrationId, onDelete, onChange, onEdit, translate],
  );

  const emptyStateProps = useMemo(
    () => ({
      title: 'No automations found',
      description: 'Create your first automation to get started',
      primaryAction: (
        <Link to={getSalesforceNewAutomationUrl(integrationId)}>
          <Button
            variant="primary"
            rightIcon={<FontAwesomeIcon icon={faPlus} />}
          >
            <FormattedMessage id="settings.integrations.sf.automation.add" />
          </Button>
        </Link>
      ),
    }),
    [integrationId],
  );

  const tableProps = useMemo(
    () => ({
      columns,
      data: (automationList || []).map((item) => ({
        ...item,
        id: item.automationId!,
      })),
      onRowClick: (record: any) => onEdit(record.automationId),
      onDragEnd: onSortChange,
      emptyText: 'No automations found',
    }),
    [columns, automationList, onEdit, onSortChange],
  );

  const headerSection = useMemo(
    () => (
      <Row justify="space-between" align="middle" gutter={16}>
        <Col>
          <Text>
            <Space align="center">
              <IconShell icon={<FontAwesomeIcon icon={faInfoCircle} />} />
              <FormattedMessage id="salesforce.automations.list.helper" />
            </Space>
          </Text>
        </Col>
        <Col>
          <Link to={getSalesforceNewAutomationUrl(integrationId)}>
            <Button
              variant="primary"
              rightIcon={<FontAwesomeIcon icon={faPlus} />}
              data-cy="integrations.salesforce.new_automation_add"
            >
              <FormattedMessage id="salesforce.automations.new.automation" />
            </Button>
          </Link>
        </Col>
      </Row>
    ),
    [integrationId],
  );

  switch (automationListStatus) {
    case '': {
      return <PageLoading />;
    }
    case 'success': {
      return automationList?.length ? (
        <div style={{ padding: '16px' }}>
          {headerSection}
          <div style={{ marginTop: '16px' }}>
            <DraggableTable
              columns={tableProps.columns}
              data={tableProps.data}
              onRowClick={tableProps.onRowClick}
              onDragEnd={tableProps.onDragEnd}
              emptyText={tableProps.emptyText}
            />
          </div>
        </div>
      ) : (
        <EmptyState
          title={emptyStateProps.title}
          description={emptyStateProps.description}
          primaryAction={emptyStateProps.primaryAction}
        />
      );
    }
    case 'error':
    default:
      return null;
  }
};
